import axios from "axios";
import { useAuth } from "../auth/useAuth";

const API_URL =
  process.env.NODE_ENV === "production"
    ? "https://www.waveofflavors.fi/api/admin"
    : "http://localhost:3001/api/admin";

export interface Booking {
  id: string;
  name: string;
  email: string;
  phone?: string;
  date: string;
  time: string;
  guests: string;
  specialRequests?: string;
  newsletter?: boolean;
  createdAt: string;
}

interface LoginResponse {
  success: boolean;
  token?: string;
  error?: string;
}

// Login function
export const login = async (
  email: string,
  password: string
): Promise<string> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_URL}/login`,
      { email, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.data.success || !response.data.token) {
      throw new Error(response.data.error || "Login failed");
    }

    return response.data.token;
  } catch (error) {
    console.error("Login error:", error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || "Invalid email or password");
    }
    throw new Error("Failed to connect to authentication service");
  }
};

// Get bookings with improved error handling and cache busting
export const getBookings = async (): Promise<Booking[]> => {
  try {
    // Safely access localStorage
    let token;
    try {
      token = localStorage.getItem("authToken");
    } catch (e) {
      console.error("localStorage access error:", e);
      throw new Error(
        "Browser storage is not available. Please check your privacy settings or try a different browser."
      );
    }

    if (!token) {
      throw new Error("Authentication required");
    }

    // Add timestamp and random number to prevent caching in Firefox and Safari
    const timestamp = new Date().getTime();
    const randomParam = Math.floor(Math.random() * 1000000);
    console.log(
      `Fetching bookings with token (timestamp: ${timestamp}, random: ${randomParam})`
    );

    const response = await axios.get(
      `${API_URL}/bookings?t=${timestamp}&r=${randomParam}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        timeout: 20000, // Increased timeout for MongoDB Atlas free tier
      }
    );

    console.log("API response:", response.status, response.statusText);

    if (!response.data.success) {
      console.error("API returned error:", response.data.error);
      throw new Error(response.data.error || "Failed to fetch bookings");
    }

    // Check if data is valid
    if (!response.data.data || !Array.isArray(response.data.data)) {
      console.error("Invalid data structure received:", response.data);
      throw new Error("Invalid data received from server");
    }

    console.log("Successfully fetched bookings:", response.data.data.length);

    // Normalize date formats to ensure consistency in the date comparison
    const normalizedBookings = response.data.data.map((booking) => {
      // Make sure date is in YYYY-MM-DD format for calendar view matching
      if (booking.date && typeof booking.date === "string") {
        const dateComponents = booking.date.split("-");
        if (dateComponents.length === 3) {
          const [year, month, day] = dateComponents;
          booking.date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        }
      }
      return booking;
    });

    return normalizedBookings;
  } catch (error) {
    console.error("Error fetching bookings:", error);

    if (axios.isAxiosError(error)) {
      // Network errors, timeouts, etc.
      if (!error.response) {
        console.error("Network error - no response received");
        throw new Error(
          "Unable to connect to the server. Please check your internet connection and try again."
        );
      }

      if (error.response.status === 401) {
        // Clear invalid token
        localStorage.removeItem("authToken");
        throw new Error("Your session has expired. Please login again.");
      }

      // Use server-provided error message when available
      if (error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
    }

    // Add specific handling for Firefox and Safari storage errors
    if (error instanceof Error && error.message.includes("storage")) {
      console.error("Browser storage error:", error);
      throw new Error(
        "Your browser may have privacy settings that prevent this app from functioning correctly. Please try allowing cookies and local storage for this site."
      );
    }

    throw error instanceof Error
      ? error
      : new Error("Failed to fetch bookings");
  }
};

// Add a new booking manually (for admin)
export const addBooking = async (
  bookingData: Omit<Booking, "id" | "createdAt">
): Promise<Booking> => {
  try {
    const token = localStorage.getItem("authToken");

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await axios.post(`${API_URL}/store-booking`, bookingData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to add booking");
    }

    return response.data.data;
  } catch (error) {
    console.error("Error adding booking:", error);
    throw error instanceof Error ? error : new Error("Failed to add booking");
  }
};

// Logout function
export const logout = () => {
  localStorage.removeItem("authToken");
};